import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { TabsContent } from '../ui/tabs';
import { Badge } from '../ui/badge';
import { Switch } from '../ui/switch';
import {
  Plus,
  Edit,
  Trash2,
  Database,
  Infinity,
  Shield,
  Globe,
  Network,
  Loader2,
  ChevronDown,
  Gauge
} from 'lucide-react';
import { Slider } from '../ui/slider';
import DistributionPreview from './DistributionPreview';

export default function AdminClientsTab({
  clients,
  clientForm,
  editingClient,
  showClientForm,
  setShowClientForm,
  setClientForm,
  resetClientForm,
  handleUpdateClient,
  handleCreateClient,
  handleEditClient,
  handleDeleteClient,
  testingWebContext,
  webContextStatus,
  handleTestWebContext,
  formatLimit
}) {
  const contextCharCount = clientForm?.company_context?.raw_text?.length || 0;

  return (
    <TabsContent value="clients" className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-foreground">Clients</h2>
        <Button
          onClick={() => { resetClientForm(); setShowClientForm(!showClientForm); }}
          className="bg-primary hover:bg-primary/90"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Client
        </Button>
      </div>

      {showClientForm && (
        <Card className="border border-border">
          <CardHeader>
            <CardTitle className="text-lg">
              {editingClient ? 'Edit Client' : 'Create New Client'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <form onSubmit={editingClient ? handleUpdateClient : handleCreateClient} className="space-y-6">
              {/* Basic Info */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="name">Client Name *</Label>
                  <Input
                    id="name"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    placeholder="Acme Corp"
                    required
                  />
                </div>
                <div>
                  <Label htmlFor="model">AI Model *</Label>
                  <Select
                    value={clientForm.model}
                    onValueChange={(value) => setClientForm({ ...clientForm, model: value })}
                  >
                    <SelectTrigger id="model">
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="gpt-4o-mini">GPT-4o Mini (Fast & Cost-Effective)</SelectItem>
                      <SelectItem value="gpt-4.1-mini">GPT-4.1 Mini (Faster, Cheaper)</SelectItem>
                      <SelectItem value="gpt-5-nano">GPT-5 Nano (Fastest & Cheapest)</SelectItem>
                      <SelectItem value="gpt-4o">GPT-4o (Balanced)</SelectItem>
                      <SelectItem value="gpt-4.1">GPT-4.1 (1M Context, Precise)</SelectItem>
                      <SelectItem value="gpt-5-mini">GPT-5 Mini (Fast & Capable)</SelectItem>
                      <SelectItem value="gpt-5.2">GPT-5.2 (Most Advanced)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={clientForm.description}
                  onChange={(e) => setClientForm({ ...clientForm, description: e.target.value })}
                  placeholder="Optional description"
                  rows={2}
                />
              </div>

              {/* Company Context */}
              <div className="space-y-3 pt-2 border-t border-border" data-testid="company-context-section">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label className="text-sm font-medium">Company Context (Optional)</Label>
                    <p className="text-xs text-muted-foreground">
                      Free‑form organizational background injected into prompts
                    </p>
                  </div>
                  <Switch
                    checked={clientForm.company_context?.is_active ?? true}
                    onCheckedChange={(checked) => setClientForm({
                      ...clientForm,
                      company_context: {
                        ...clientForm.company_context,
                        is_active: checked
                      }
                    })}
                    className="data-[state=checked]:bg-primary"
                    data-testid="company-context-toggle"
                  />
                </div>
                <div>
                  <Label htmlFor="company_context" className="text-xs text-muted-foreground">Company Context</Label>
                  <Textarea
                    id="company_context"
                    value={clientForm.company_context?.raw_text || ''}
                    onChange={(e) => setClientForm({
                      ...clientForm,
                      company_context: {
                        ...clientForm.company_context,
                        raw_text: e.target.value
                      }
                    })}
                    placeholder="Describe the organization, culture, recent events, policies, etc."
                    rows={4}
                    maxLength={5000}
                    data-testid="company-context-textarea"
                  />
                  <p className="text-[10px] text-muted-foreground text-right">{contextCharCount}/5000</p>
                </div>
                <div>
                  <Label htmlFor="company_context_notes" className="text-xs text-muted-foreground">Additional Notes (Internal)</Label>
                  <Textarea
                    id="company_context_notes"
                    value={clientForm.company_context?.additional_notes || ''}
                    onChange={(e) => setClientForm({
                      ...clientForm,
                      company_context: {
                        ...clientForm.company_context,
                        additional_notes: e.target.value
                      }
                    })}
                    placeholder="Optional internal notes (not injected into prompts)"
                    rows={2}
                    maxLength={2000}
                    data-testid="company-context-notes"
                  />
                </div>
              </div>

              {/* Usage Limits Section */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Database className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Usage Limits</Label>
                  <span className="text-xs text-muted-foreground">(Leave empty for unlimited)</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="max_populations" className="text-xs text-muted-foreground">
                      Max Populations
                    </Label>
                    <div className="relative">
                      <Input
                        id="max_populations"
                        type="number"
                        min="0"
                        value={clientForm.max_populations}
                        onChange={(e) => setClientForm({ ...clientForm, max_populations: e.target.value })}
                        placeholder="∞"
                        className="pr-8"
                      />
                      {!clientForm.max_populations && (
                        <Infinity className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="max_stimuli" className="text-xs text-muted-foreground">
                      Max Stimuli
                    </Label>
                    <div className="relative">
                      <Input
                        id="max_stimuli"
                        type="number"
                        min="0"
                        value={clientForm.max_stimuli}
                        onChange={(e) => setClientForm({ ...clientForm, max_stimuli: e.target.value })}
                        placeholder="∞"
                        className="pr-8"
                      />
                      {!clientForm.max_stimuli && (
                        <Infinity className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="max_simulations" className="text-xs text-muted-foreground">
                      Max Simulations
                    </Label>
                    <div className="relative">
                      <Input
                        id="max_simulations"
                        type="number"
                        min="0"
                        value={clientForm.max_simulations}
                        onChange={(e) => setClientForm({ ...clientForm, max_simulations: e.target.value })}
                        placeholder="∞"
                        className="pr-8"
                      />
                      {!clientForm.max_simulations && (
                        <Infinity className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      )}
                    </div>
                  </div>
                </div>
                {/* Monthly Token Budget */}
                <div>
                  <Label htmlFor="monthly_token_budget" className="text-xs text-muted-foreground">
                    Monthly Token Budget
                  </Label>
                  <div className="relative">
                    <Input
                      id="monthly_token_budget"
                      type="number"
                      min="0"
                      step="1000"
                      value={clientForm.monthly_token_budget ?? ''}
                      onChange={(e) => setClientForm({ ...clientForm, monthly_token_budget: e.target.value })}
                      placeholder="No budget (unlimited)"
                      className="pr-8"
                      data-testid="monthly-token-budget-input"
                    />
                    {(!clientForm.monthly_token_budget && clientForm.monthly_token_budget !== 0) && (
                      <Infinity className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    )}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Soft warning at 80%, hard block on simulation runs at 100%. Resets at the start of each calendar month (UTC).
                  </p>
                </div>
              </div>

              {/* Temperature Settings */}
              <div className="space-y-3">
                <div className="flex items-center gap-2">
                  <Label className="text-sm font-medium">AI Settings</Label>
                  <span className="text-xs text-muted-foreground">(overrides system defaults)</span>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <Label htmlFor="temp_min">Temperature Min</Label>
                    <Input
                      id="temp_min"
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={clientForm.temperature_min}
                      onChange={(e) => setClientForm({ ...clientForm, temperature_min: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="temp_max">Temperature Max</Label>
                    <Input
                      id="temp_max"
                      type="number"
                      step="0.1"
                      min="0"
                      max="1"
                      value={clientForm.temperature_max}
                      onChange={(e) => setClientForm({ ...clientForm, temperature_max: parseFloat(e.target.value) })}
                    />
                  </div>
                  <div>
                    <Label htmlFor="distribution">Distribution</Label>
                    <Select
                      value={clientForm.temperature_distribution}
                      onValueChange={(value) => setClientForm({ ...clientForm, temperature_distribution: value })}
                    >
                      <SelectTrigger id="distribution" data-testid="distribution-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="normal">Normal</SelectItem>
                        <SelectItem value="uniform">Uniform</SelectItem>
                        <SelectItem value="truncated_normal">Truncated Normal</SelectItem>
                        <SelectItem value="beta">Beta</SelectItem>
                        <SelectItem value="bimodal">Bimodal</SelectItem>
                        <SelectItem value="triangular">Triangular</SelectItem>
                        <SelectItem value="skewed_normal">Skewed Normal</SelectItem>
                        <SelectItem value="log_normal">Log-Normal</SelectItem>
                        <SelectItem value="exponential">Exponential</SelectItem>
                        <SelectItem value="discrete_step">Discrete Step</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Polarization Strength — visible when bimodal is selected */}
                {clientForm.temperature_distribution === 'bimodal' && (
                  <div className="space-y-2 p-3 rounded-lg border border-border bg-muted/30">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Gauge className="w-4 h-4 text-muted-foreground" />
                        <Label className="text-sm font-medium">Polarization Strength</Label>
                      </div>
                      <span className="text-sm font-mono text-muted-foreground" data-testid="polarization-value">
                        {clientForm.polarization_strength?.toFixed(2) ?? '0.50'}
                      </span>
                    </div>
                    <Slider
                      value={[clientForm.polarization_strength ?? 0.5]}
                      onValueChange={([v]) => setClientForm({ ...clientForm, polarization_strength: v })}
                      min={0}
                      max={1}
                      step={0.05}
                      className="w-full"
                      data-testid="polarization-slider"
                    />
                    <div className="flex justify-between text-[10px] text-muted-foreground">
                      <span>0 — single peak (~ normal)</span>
                      <span>1 — extreme polarization</span>
                    </div>
                  </div>
                )}

                {/* Distribution shape preview chart */}
                <DistributionPreview
                  distribution={clientForm.temperature_distribution}
                  polarization={clientForm.polarization_strength ?? 0.5}
                />
              </div>

              <div>
                <Label htmlFor="api_key">Client API Key (optional)</Label>
                <Input
                  id="api_key"
                  type="password"
                  value={clientForm.api_key}
                  onChange={(e) => setClientForm({ ...clientForm, api_key: e.target.value })}
                  placeholder="sk-..."
                />
                <p className="text-xs text-muted-foreground mt-1">Leave empty to use global key</p>
              </div>

              <div>
                <Label>Enabled Modules</Label>
                <div className="flex flex-wrap gap-2 mt-1.5">
                  {[
                    { id: 'people_lab', label: 'PeopleLab', desc: 'HR simulations', color: 'blue' },
                    { id: 'market_lab', label: 'MarketLab', desc: 'Marketing research', color: 'green' },
                    { id: 'pharma_lab', label: 'PharmaLab', desc: 'Pharma / healthcare', color: 'teal' },
                  ].map(mod => {
                    const isActive = (clientForm.enabled_modules || []).includes(mod.id);
                    return (
                      <button
                        key={mod.id}
                        type="button"
                        data-testid={`module-toggle-${mod.id}`}
                        onClick={() => {
                          const current = clientForm.enabled_modules || [];
                          const next = isActive
                            ? current.filter(m => m !== mod.id)
                            : [...current, mod.id];
                          if (next.length === 0) return; // Must have at least one
                          setClientForm({ ...clientForm, enabled_modules: next });
                          // Also set reporting_mode to first module for backward compat
                          const modeMap = { people_lab: 'peoplelab', market_lab: 'marketlab', pharma_lab: 'peoplelab' };
                          setClientForm(prev => ({ ...prev, enabled_modules: next, reporting_mode: modeMap[next[0]] || 'peoplelab' }));
                        }}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-all ${
                          isActive
                            ? `border-${mod.color}-400 dark:border-${mod.color}-600 bg-${mod.color}-50 dark:bg-${mod.color}-500/10 ring-1 ring-${mod.color}-300 dark:ring-${mod.color}-700`
                            : 'border-border bg-background hover:bg-muted/50'
                        }`}
                      >
                        <div className={`w-2 h-2 rounded-full ${isActive ? `bg-${mod.color}-500` : 'bg-muted-foreground/30'}`} />
                        <div className="text-left">
                          <span className={`font-medium ${isActive ? 'text-foreground' : 'text-muted-foreground'}`}>{mod.label}</span>
                          <span className="text-xs text-muted-foreground ml-1.5">{mod.desc}</span>
                        </div>
                      </button>
                    );
                  })}
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">Select which reporting modules are available for this client</p>
              </div>

              {/* Security Settings */}
              <div className="space-y-3 pt-2 border-t border-border">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Security Settings</Label>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="mfa_required" className="text-sm font-medium">
                      Require Two-Factor Authentication
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      All users in this client must enable 2FA to access the platform
                    </p>
                  </div>
                  <Switch
                    id="mfa_required"
                    checked={clientForm.mfa_required}
                    onCheckedChange={(checked) => setClientForm({ ...clientForm, mfa_required: checked })}
                    className="data-[state=checked]:bg-primary"
                  />
                </div>
              </div>

              {/* Web Context Enrichment */}
              <div className="space-y-3 pt-2 border-t border-border" data-testid="web-context-settings">
                <div className="flex items-center gap-2">
                  <Globe className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Web Context Enrichment</Label>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="web_context_enabled" className="text-sm font-medium">
                      Enable Web Context
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Allow stimuli to be enriched with external web knowledge
                    </p>
                  </div>
                  <Switch
                    id="web_context_enabled"
                    checked={clientForm.web_context_enabled}
                    onCheckedChange={(checked) => setClientForm({ ...clientForm, web_context_enabled: checked })}
                    className="data-[state=checked]:bg-primary"
                    data-testid="web-context-toggle"
                  />
                </div>
                {clientForm.web_context_enabled && (
                  <div className="space-y-3 pl-1">
                    <div>
                      <Label className="text-xs text-muted-foreground">Provider</Label>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="outline" className="text-xs">OpenAI Web Search</Badge>
                      </div>
                    </div>
                    <div>
                      <Label htmlFor="wc_api_key" className="text-xs">API Key (optional — falls back to client key)</Label>
                      <Input
                        id="wc_api_key"
                        type="password"
                        value={clientForm.web_context_api_key}
                        onChange={(e) => setClientForm({ ...clientForm, web_context_api_key: e.target.value })}
                        placeholder="sk-..."
                        className="mt-1"
                        data-testid="web-context-api-key"
                      />
                    </div>
                    {editingClient && (
                      <div className="flex items-center gap-2">
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={() => handleTestWebContext(editingClient.id)}
                          disabled={testingWebContext}
                          data-testid="test-web-context-btn"
                        >
                          {testingWebContext ? <Loader2 className="w-3 h-3 animate-spin mr-1" /> : null}
                          Test Connection
                        </Button>
                        {webContextStatus && (
                          <Badge className={webContextStatus.status === 'connected' ? 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300' : 'bg-red-100 dark:bg-red-500/20 text-red-700 dark:text-red-300'}>
                            {webContextStatus.status === 'connected' ? 'Connected' : 'Error'}
                          </Badge>
                        )}
                        {editingClient.web_context_api_key_preview && (
                          <span className="text-xs text-muted-foreground">Current: {editingClient.web_context_api_key_preview}</span>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Social Interaction Engine */}
              <div className="space-y-3 pt-2 border-t border-border" data-testid="social-interactions-settings">
                <div className="flex items-center gap-2">
                  <Network className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">Social Interaction Engine</Label>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="social_interactions_enabled" className="text-sm font-medium">
                      Enable Social Interactions
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Model peer influence dynamics in population simulations
                    </p>
                  </div>
                  <Switch
                    id="social_interactions_enabled"
                    checked={clientForm.social_interactions_enabled}
                    onCheckedChange={(checked) => setClientForm({ ...clientForm, social_interactions_enabled: checked })}
                    className="data-[state=checked]:bg-primary"
                    data-testid="social-interactions-toggle"
                  />
                </div>
              </div>

              {/* AI Profile Enrichment (admin-gated, advanced) */}
              <div className="space-y-3 pt-2 border-t border-border" data-testid="synthetic-attributes-settings">
                <div className="flex items-center gap-2">
                  <Network className="w-4 h-4 text-muted-foreground" />
                  <Label className="text-sm font-medium">AI Profile Enrichment</Label>
                </div>
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label htmlFor="synthetic_attributes_enabled" className="text-sm font-medium">
                      Enable AI Profile Enrichment
                    </Label>
                    <p className="text-xs text-muted-foreground">
                      Allow AI-inferred psychological / socioeconomic / work attributes for this client&apos;s populations.
                      Stored separately from uploaded data and clearly labelled as inferred.
                    </p>
                  </div>
                  <Switch
                    id="synthetic_attributes_enabled"
                    checked={clientForm.synthetic_attributes_enabled || false}
                    onCheckedChange={(checked) => setClientForm({ ...clientForm, synthetic_attributes_enabled: checked })}
                    className="data-[state=checked]:bg-primary"
                    data-testid="synthetic-attributes-toggle"
                  />
                </div>
              </div>

              <div className="flex gap-2">
                <Button type="submit" className="bg-primary hover:bg-primary/90">
                  {editingClient ? 'Update' : 'Create'}
                </Button>
                <Button type="button" variant="outline" onClick={() => { setShowClientForm(false); resetClientForm(); }}>
                  Cancel
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {clients.map(client => (
          <Card key={client.id} className="border border-border hover:shadow-md transition-shadow">
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div>
                  <CardTitle className="text-lg text-foreground">{client.name}</CardTitle>
                  <p className="text-sm text-muted-foreground mt-1">{client.description}</p>
                </div>
                <div className="flex gap-1">
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleEditClient(client)}
                    className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  >
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDeleteClient(client.id)}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 dark:hover:bg-red-500/10"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              {/* Usage Stats */}
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Users:</span>
                  <Badge variant="outline">{client.user_count}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Reporting:</span>
                  {(client.enabled_modules || [client.reporting_mode === 'marketlab' ? 'market_lab' : 'people_lab']).map(mod => {
                    const labelMap = { people_lab: 'PeopleLab', market_lab: 'MarketLab', pharma_lab: 'PharmaLab' };
                    const colorMap = { 
                      people_lab: 'bg-blue-100 dark:bg-blue-500/20 text-blue-700 dark:text-blue-300',
                      market_lab: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
                      pharma_lab: 'bg-emerald-100 dark:bg-emerald-500/20 text-emerald-700 dark:text-emerald-300',
                    };
                    return (
                      <Badge key={mod} className={colorMap[mod] || 'bg-slate-100 text-slate-700'}>
                        {labelMap[mod] || mod}
                      </Badge>
                    );
                  })}
                </div>
              </div>

              {/* MFA Badge */}
              {client.mfa_required && (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-emerald-50 dark:bg-emerald-950/30 rounded border border-emerald-200 dark:border-emerald-800">
                  <Shield className="w-3.5 h-3.5 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-700 dark:text-emerald-400">2FA Required</span>
                </div>
              )}
              {client.web_context_enabled && (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 rounded border border-blue-200" data-testid="web-context-badge">
                  <Globe className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">Web Context Enabled</span>
                </div>
              )}
              {client.social_interactions_enabled && (
                <div className="flex items-center gap-2 px-2 py-1.5 bg-blue-50 rounded border border-blue-200" data-testid="social-interactions-badge">
                  <Network className="w-3.5 h-3.5 text-blue-600" />
                  <span className="text-xs font-medium text-blue-700">Social Interactions</span>
                </div>
              )}

              {/* Resource Usage with Limits */}
              <div className="space-y-2 pt-2 border-t border-border">
                <p className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Resource Usage</p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center p-2 bg-secondary/50 rounded">
                    <p className="text-lg font-semibold text-foreground">
                      {client.population_count}
                      <span className="text-xs text-muted-foreground">/{formatLimit(client.max_populations)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">Populations</p>
                  </div>
                  <div className="text-center p-2 bg-secondary/50 rounded">
                    <p className="text-lg font-semibold text-foreground">
                      {client.stimuli_count || 0}
                      <span className="text-xs text-muted-foreground">/{formatLimit(client.max_stimuli)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">Stimuli</p>
                  </div>
                  <div className="text-center p-2 bg-secondary/50 rounded">
                    <p className="text-lg font-semibold text-foreground">
                      {client.simulation_count}
                      <span className="text-xs text-muted-foreground">/{formatLimit(client.max_simulations)}</span>
                    </p>
                    <p className="text-xs text-muted-foreground">Simulations</p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </TabsContent>
  );
}